import { useI18n } from '@/hooks/useI18n';
import { UserCheck, UserPlus, UserX, Loader2 } from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { CustomerInfo } from "./components/CustomerInfo";
import type { ClientAccountState } from "@/lib/shared-proposal/hooks/useClientAccountState";
import type { MailLanguage } from "@/lib/shared-proposal/hooks/useClientMailLanguage";
import { MailLanguageSelector } from "@/lib/mail/MailLanguageSelector";

interface SendQuoteStepClientProps {
    itinerary: DashboardItinerary;
    accountState?: ClientAccountState;
    isCheckingUser?: boolean;
    mailLanguage: MailLanguage;
    onMailLanguageChange: (value: MailLanguage) => void;
    /** Lingua sul profilo del cliente, `null` se non ha ancora un profilo. */
    clientProfileLanguage: MailLanguage | null;
    isCheckingMailLanguage?: boolean;
    mailLanguageLookupFailed?: boolean;
}

/**
 * Step 1: chi e' il cliente. Anagrafica, stato dell'account (decide se la
 * proposta porta le credenziali di primo accesso) e lingua con cui gli si scrive.
 */
export const SendQuoteStepClient = ({
    itinerary,
    accountState = 'none',
    isCheckingUser,
    mailLanguage,
    onMailLanguageChange,
    clientProfileLanguage,
    isCheckingMailLanguage,
    mailLanguageLookupFailed,
}: SendQuoteStepClientProps) => {
    const { t } = useI18n();

    return (
        <div className="space-y-6">
            <CustomerInfo itinerary={itinerary} />

            {isCheckingUser ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <span>{t('itinerary.checkingUserExists')}</span>
                </div>
            ) : accountState === 'active' ? (
                <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-green-200 bg-green-50">
                    <UserCheck className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-green-800">{t('itinerary.userExistsTitle')}</p>
                        <p className="text-green-700 mt-0.5">{t('itinerary.userExistsInfo')}</p>
                    </div>
                </div>
            ) : accountState === 'invited' ? (
                /* Anagrafato alla creazione della travel request, ma mai entrato:
                   con la proposta riceve le credenziali di primo accesso. */
                <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50">
                    <UserPlus className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-blue-800">{t('itinerary.userInvitedTitle')}</p>
                        <p className="text-blue-700 mt-0.5">{t('itinerary.userInvitedInfo')}</p>
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50">
                    <UserX className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-amber-800">{t('itinerary.userNotFoundTitle')}</p>
                        <p className="text-amber-700 mt-0.5">{t('itinerary.userNotFoundInfo')}</p>
                    </div>
                </div>
            )}

            <MailLanguageSelector
                value={mailLanguage}
                onChange={onMailLanguageChange}
                profileLanguage={clientProfileLanguage}
                isLoading={isCheckingMailLanguage}
                lookupFailed={mailLanguageLookupFailed}
            />
        </div>
    );
};
